import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../lib/api-client'
import type { AxiosError } from 'axios'

export interface Task {
  id: number
  projectId: number
  boqItemId?: number
  taskName: string
  startDate: string
  endDate: string
  duration: number
  plannedWeight: number
  actualWeight?: number
  status: 'planned' | 'in_progress' | 'completed' | 'delayed'
  resources?: {
    personil?: number
    equipment?: string[]
    budget?: number
  }
  dependencies?: number[]
  sortOrder?: number
  progress?: number
}

export interface TaskStats {
  projectId: number
  totalTasks: number
  completedTasks: number
  inProgressTasks: number
  delayedTasks: number
  tasks: Task[]
  criticalPath: number[]
}

export interface PerformanceKPI {
  projectId: number
  totalTasks: number
  completedTasks: number
  inProgressTasks: number
  delayedTasks: number
  completionRate: number
  delayRate: number
  onTimeRate: number
  averageTaskDuration: number
  averageTaskProgress: number
}

export interface RescheduleImpact {
  originalStartDate: string
  originalEndDate: string
  newStartDate: string
  newEndDate: string
  delayDays: number
  additionalOperationalCost: number
  impactedTaskIds: number[]
  estimatedScheduleSlippage: number
  estimatedCostIncrease: number
}

// ============================================
// Query Hooks
// ============================================

/**
 * Hook to fetch all tasks for project
 */
export function useTasks(projectId: number) {
  return useQuery<TaskStats, AxiosError>({
    queryKey: ['tasks', projectId],
    queryFn: async () => {
      const response = await apiClient.getTasks(projectId)
      return response.data
    },
    enabled: !!projectId,
    staleTime: import.meta.env.VITE_TASKS_QUERY_STALE_TIME
      ? parseInt(import.meta.env.VITE_TASKS_QUERY_STALE_TIME)
      : 5 * 60 * 1000
  })
}

/**
 * Hook to fetch performance KPI
 */
export function usePerformanceKPI(projectId: number) {
  return useQuery<PerformanceKPI, AxiosError>({
    queryKey: ['performance-kpi', projectId],
    queryFn: async () => {
      const response = await apiClient.getPerformanceKPI(projectId)
      return response.data
    },
    enabled: !!projectId,
    staleTime: import.meta.env.VITE_KPI_QUERY_STALE_TIME
      ? parseInt(import.meta.env.VITE_KPI_QUERY_STALE_TIME)
      : 10 * 60 * 1000
  })
}

/**
 * Hook to fetch critical path
 */
export function useCriticalPath(projectId: number) {
  return useQuery<any, AxiosError>({
    queryKey: ['critical-path', projectId],
    queryFn: async () => {
      const response = await apiClient.getCriticalPath(projectId)
      return response.data
    },
    enabled: !!projectId
  })
}

// ============================================
// Mutation Hooks
// ============================================

/**
 * Hook to generate tasks from BoQ
 */
export function useGenerateTasks(projectId: number) {
  const queryClient = useQueryClient()
  
  return useMutation<any, AxiosError, { deleteExisting?: boolean }>({
    mutationFn: async (data) => {
      const response = await apiClient.generateTasksFromBoQ(projectId, data.deleteExisting)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['tasks', projectId]
      })
      queryClient.invalidateQueries({
        queryKey: ['performance-kpi', projectId]
      })
    },
    onError: (error) => {
      console.error('Failed to generate tasks:', error.response?.data)
    }
  })
}

/**
 * Hook to update task status
 */
export function useUpdateTaskStatus(projectId: number) {
  const queryClient = useQueryClient()
  
  return useMutation<
    Task,
    AxiosError,
    { taskId: number; status: 'planned' | 'in_progress' | 'completed' | 'delayed' }
  >({
    mutationFn: async ({ taskId, status }) => {
      const response = await apiClient.updateTaskStatus(taskId, status)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['tasks', projectId]
      })
      queryClient.invalidateQueries({
        queryKey: ['performance-kpi', projectId]
      })
    },
    onError: (error) => {
      console.error('Failed to update task status:', error.response?.data)
    }
  })
}

/**
 * Hook to update task resources
 */
export function useUpdateTaskResources(projectId: number) {
  const queryClient = useQueryClient()
  
  return useMutation<
    Task,
    AxiosError,
    {
      taskId: number
      resources: { personil?: number; equipment?: string[]; budget?: number }
    }
  >({
    mutationFn: async ({ taskId, resources }) => {
      const response = await apiClient.updateTaskResources(taskId, resources)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['tasks', projectId]
      })
    },
    onError: (error) => {
      console.error('Failed to update task resources:', error.response?.data)
    }
  })
}

/**
 * Hook to calculate reschedule impact
 */
export function useCalculateRescheduleImpact(projectId: number) {
  return useMutation<
    RescheduleImpact,
    AxiosError,
    { taskId: number; delayDays: number; costPerDay?: number }
  >({
    mutationFn: async ({ taskId, delayDays, costPerDay }) => {
      const response = await apiClient.calculateRescheduleImpact(taskId, delayDays, costPerDay)
      return response.data
    },
    onError: (error) => {
      console.error('Failed to calculate impact:', error.response?.data)
    }
  })
}

/**
 * Hook to create new task
 */
export function useCreateTask(projectId: number) {
  const queryClient = useQueryClient()
  
  return useMutation<Task, AxiosError, Partial<Task>>({
    mutationFn: async (taskData) => {
      const response = await apiClient.createTask({
        projectId,
        ...taskData
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['tasks', projectId]
      })
    },
    onError: (error) => {
      console.error('Failed to create task:', error.response?.data)
    }
  })
}

/**
 * Hook to delete task
 */
export function useDeleteTask(projectId: number) {
  const queryClient = useQueryClient()
  
  return useMutation<{ success: boolean }, AxiosError, number>({
    mutationFn: async (taskId) => {
      const response = await apiClient.deleteTask(taskId)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['tasks', projectId]
      })
    },
    onError: (error) => {
      console.error('Failed to delete task:', error.response?.data)
    }
  })
}